import type { ComponentPropsWithRef } from 'react';
import { cn } from '../lib/cn';

/**
 * - `thin`    3px, no radius — the opname header bar under the dark header
 * - `default` 8px, radius 4  — inspector capacity on the dashboard
 */
export type ProgressBarSize = 'thin' | 'default';

/** `light` sits on a card, `dark` on an ink header. */
export type ProgressBarSurface = 'light' | 'dark';

export type ProgressBarProps = Omit<ComponentPropsWithRef<'div'>, 'children' | 'role'> & {
    /** 0–100. Clamped. */
    value: number;
    size?: ProgressBarSize;
    surface?: ProgressBarSurface;
    /**
     * What the bar measures, for screen readers: "Voortgang opname",
     * "Bezetting Mark Dekker". Required — a naked progressbar says nothing.
     */
    label: string;
    /** Fill colour. `accent` always, `warn` only when the bar itself is the warning. */
    tone?: 'accent' | 'warn';
};

const trackSurface: Record<ProgressBarSurface, string> = {
    light: 'bg-[var(--border-subtle)]',
    dark: 'bg-[var(--ink-600)]',
};

export function ProgressBar({
    value,
    size = 'default',
    surface = 'light',
    tone = 'accent',
    label,
    className,
    ...rest
}: ProgressBarProps) {
    const pct = Math.max(0, Math.min(100, Math.round(value)));

    return (
        <div
            role="progressbar"
            aria-label={label}
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={`${pct}%`}
            className={cn(
                'w-full overflow-hidden',
                size === 'thin' ? 'h-[3px] rounded-none' : 'h-2 rounded-[4px]',
                trackSurface[surface],
                className,
            )}
            {...rest}
        >
            <div
                className={cn(
                    'h-full',
                    size === 'thin' ? 'rounded-none' : 'rounded-[4px]',
                    tone === 'warn' ? 'bg-[var(--warn)]' : 'bg-[var(--accent)]',
                )}
                style={{ width: `${pct}%` }}
            />
        </div>
    );
}
